import { useEffect, useState } from 'react';
import { FormControl, makeStyles, MenuItem, Select } from '@material-ui/core';

import lang from '../../../../lib/language';
import { TopGainersAndLosersPriceOption } from '../../../../lib/models/fundamental/types';

import iconExpandUp from '../../../../assets/img/icon-expand-more_24px.svg';
import iconFilter from '../../../../assets/img/icon-filter_24px.svg';
import { ClickableDiv, MobileDrawer } from '../../../../components';
import { DEFAULT_FONT_FAMILY } from '../../../../constants';
import { useBrowserWidth, useIsMobile } from '../../../../hooks';
import { ZOOM_BEHAVIOR_WIDTH } from '../../../../util/constants';

import { FILTER_RANGE_OPTIONS } from './FilterDropDownOptions';

import styles from '../../TopGainersAndLosers.module.scss';

const useStyles = makeStyles<null, Props>(() => ({
  select: {
    boxShadow: 'none',
    borderRadius: '0px 6px 6px 0px',
    padding: '7px 0px',
    fontSize: '16px',
    fontFamily: DEFAULT_FONT_FAMILY,
    backgroundColor: 'transparent',

    '&, &:before, &:after': {
      color: props => (props.isElliptic ? '#F2F4F7' : '#3BB0FF'),
      borderRadius: '0px 6px 6px 0px',
    },

    '&, &:active, &:focus': {
      background: (p) => {
        if (p.hasBackground) {
          return 'transparent';
        }
        return 'none';
      },
      borderRadius: props => (props.isElliptic ? '0px 18px 18px 0px' : '0px 6px 6px 0px'),
    },
  },

  paper: {
    border: '1px solid #3BB0FF',
    background: '#1D3960',
  },

  wrapper: {
    display: 'flex',
    backgroundColor: props => {
      if (props.isEllipticAndMobile) {
        return '#3BB0FF1A';
      }
      return props.isElliptic ? '#3BB0FF1A' : undefined;
    },
    padding: props => (props.isElliptic ? 2.84 : undefined),
    borderRadius: props => (props.isElliptic ? '32px 32px 32px 32px' : undefined),
    border: props => (props.isElliptic ? '1px solid #3BB0FF26' : undefined),
  },

  iconWrapper: {
    background: (p) => {
      if (p.hasBackground) {
        return 'transparent';
      }
      return 'none';
    },
    borderRadius: props => (props.isElliptic ? '18px 0px 0px 18px' : '2px 0px 0px 2px'),
    display: 'inline-block',
    height: '33px',
  },

  icon: {
    marginTop: '4px',
    margin: '0px 6px 0px 12px',
  },

  option: {
    height: '56px',
    color: '#F2F4F7',
    fontFamily: DEFAULT_FONT_FAMILY,

    '&:first-child': {
      marginTop: '-8px',
    },

    '&:last-child': {
      marginBottom: '-8px',
    },

    '&:hover': {
      opacity: 1,
    },
  },

  selected: {
    color: '#CBD5E0',
    opacity: 1,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: '16px',
  },
}));

type Props = {
    handleFilterOption: (option: number[], optionValue: string) => void;
    hasBackground: boolean;
    hasExpandArrow: boolean;
    optionValue?: string;
    currentTabTopMovers?: number;
    isDisabled?: boolean;
    isElliptic?: boolean;
    isEllipticAndMobile?: boolean;
    isDiscover?: boolean;
}

const FilterDropDown = (compProps: Props) => {
  const {
    handleFilterOption,
    hasBackground,
    hasExpandArrow,
    optionValue,
    currentTabTopMovers,
    isDisabled,
    isElliptic,
    isEllipticAndMobile,
  } = compProps;
  const classes = useStyles(compProps);
  const { browserWidthSize } = useBrowserWidth();
  const { isMobile } = useIsMobile();
  const [value, setValue] = useState<string>(optionValue || TopGainersAndLosersPriceOption.fifteenToFifty);
  const [paperDropDown, setPaperDropDown] = useState<number>(90);
  const [isFilterModal, setFilterModal] = useState<boolean>(false);

  useEffect(() => {
    !optionValue && setValue(TopGainersAndLosersPriceOption.fifteenToFifty);
  }, [currentTabTopMovers, optionValue]);

  const handleChange = (event) => {
    const indexOption = Object.values(TopGainersAndLosersPriceOption).indexOf(event.target.value);
    handleFilterOption(FILTER_RANGE_OPTIONS[indexOption], event.target.value);
    setValue(event.target.value);
  };

  useEffect(() => {
    setPaperDropDown(90 - Math.floor(window.innerWidth / 10));
  }, [browserWidthSize]);

  return (
    <div className={classes.wrapper}>
      {!isMobile && (
        <div className={classes.iconWrapper}>
          {isElliptic ? <div className={classes.icon} />
            : <img className={isMobile ? '' : classes.icon} src={iconFilter} alt="icon filter" />}
        </div>
      )}
      {isMobile ? (
        <>
          <ClickableDiv
            disabled={isDisabled}
            className={styles.mobFilterButton}
            onClick={() => !isDisabled && setFilterModal(true)}
          >
            <span className={styles.mobFilterButtonText}>{value}</span>
          </ClickableDiv>
          <MobileDrawer
            isOpen={isFilterModal}
            title={lang.mobileSortButton()}
            onClose={() => setFilterModal(false)}
            usePortal
          >
            <div className={styles.rangeButtonWrapper}>
              {
              Object.values(TopGainersAndLosersPriceOption).map((option) => (
                <ClickableDiv
                  className={styles.rangeButton}
                  key={option}
                  onClick={() => {
                    const indexOption = Object.values(TopGainersAndLosersPriceOption).indexOf(option);
                    handleFilterOption(FILTER_RANGE_OPTIONS[indexOption], option);
                    setValue(option);
                    setFilterModal(false);
                  }}
                >
                  <span className={option === value ? styles.rangeButtonTextActive : styles.rangeButtonText}>
                    {option}
                  </span>
                </ClickableDiv>
              ))
            }
            </div>
          </MobileDrawer>
        </>
      ) : (
        <FormControl>
          <Select
            disabled={isDisabled}
            value={value}
            disableUnderline
            displayEmpty
            onChange={handleChange}
            classes={{ select: classes.select }}
            IconComponent={(props) =>
              (hasExpandArrow
                ? <div {...props}><img src={iconExpandUp} alt="icon filter" /></div>
                : <></>)}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              PaperProps: {
                style: isMobile
                  ? {}
                  : {
                    width: 170,
                    marginLeft: (browserWidthSize <= ZOOM_BEHAVIOR_WIDTH && !hasBackground) ? `${paperDropDown + 5}%` : -42,
                    zoom: browserWidthSize <= ZOOM_BEHAVIOR_WIDTH ? `${Math.floor(browserWidthSize / 10).toString()}%` : '',
                    fontFamily: DEFAULT_FONT_FAMILY,
                  },
              },
              classes: {
                paper: classes.paper,
              },
              getContentAnchorEl: null,
            }}
          >
            {
            Object.values(TopGainersAndLosersPriceOption).map((option) => (
              <MenuItem
                key={option}
                value={option}
                classes={{
                  root: classes.option,
                  selected: classes.selected,
                }}
              >
                <span>{option}</span>
              </MenuItem>
            ))
          }
          </Select>
        </FormControl>
      )}
    </div>
  );
};

export default FilterDropDown;
